// diagnostics.js — technische Infos für das Feedback, nur auf Wunsch (Schalter, Standard aus).
// Ringpuffer der letzten Client-Fehler nur im Speicher; Bereinigung als Zwilling von lib/feedback.js (Gleichheit per Test).
import { VERSION, BUILD_TIME } from './build-info.js';

export const LIMITS = { errors: 20, msg: 200, stack: 600, field: 300, total: 16384 };
const SENSITIVE_PARAM = /token|key|code|sig|secret|pass|auth|session|mail|magic|reset|^t$/i;

function cleanText(s, n) {
  if (typeof s !== 'string') return null;
  return s.replace(/(https?:\/\/[^\s?#)'"]+)[?#][^\s)'"]*/g, '$1')
    .replace(/[\w.+-]+@[\w-]+\.[\w.-]+/g, '[email]')
    .slice(0, n);
}

export function cleanPage(u) {
  let url;
  try { url = new URL(String(u || '')); } catch { return null; }
  if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
  const path = url.pathname.split('/').map((p) => (/^[A-Za-z0-9_-]{20,}$/.test(p) ? ':token' : p)).join('/');
  const q = new URLSearchParams();
  for (const [k, v] of url.searchParams) if (!SENSITIVE_PARAM.test(k) && v.length <= 32) q.append(k, v);
  const qs = q.toString();
  return (url.origin + path + (qs ? '?' + qs : '')).slice(0, LIMITS.field);
}

export function cleanError(e) {
  const o = e && typeof e === 'object' ? e : {};
  const t = typeof o.t === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(o.t) && !Number.isNaN(Date.parse(o.t)) ? new Date(o.t).toISOString() : null;
  return { t, msg: cleanText(o.msg, LIMITS.msg), stack: cleanText(o.stack, LIMITS.stack) };
}

// ---------- Ringpuffer (nur Speicher, nie gesendet ohne Schalter) ----------
const buffer = [];
export function recordError(msg, stack) {
  buffer.push(cleanError({ t: new Date().toISOString(), msg: String(msg || ''), stack: stack ? String(stack) : '' }));
  if (buffer.length > LIMITS.errors) buffer.splice(0, buffer.length - LIMITS.errors);
}
export const recentErrors = () => buffer.slice();
export const _resetErrors = () => { buffer.length = 0; };

let installed = false;
/** Einmal je Seite: window error + unhandledrejection mitschreiben. */
export function installErrorBuffer(target = typeof window !== 'undefined' ? window : null) {
  if (installed || !target) return;
  installed = true;
  target.addEventListener('error', (e) => recordError(e.message || (e.error && e.error.message) || 'error', e.error && e.error.stack));
  target.addEventListener('unhandledrejection', (e) => {
    const r = e.reason;
    recordError(r && r.message ? r.message : String(r), r && r.stack);
  });
}

// ---------- Fehlerbericht an den Server (v499) ----------
// Unbehandelte Fehler gehen bereinigt an /api/client-errors: Meldung, erste Stapelzeile, Seitenpfad ohne Parameter.
// Kein Konto, keine Browserkennung; der Server zählt nur je Fehlerart (lib/app-errors.js). Je Seitenaufruf höchstens
// REPORT_MAX verschiedene Fehler, gebündelt nach REPORT_DELAY ms.
export const REPORT_MAX = 10;
export const REPORT_DELAY = 2000;
export function createReporter({ send, delay = REPORT_DELAY, max = REPORT_MAX, page = () => (typeof location !== 'undefined' ? location.pathname : '') } = {}) {
  const seen = new Set();
  let queue = [], timer = null;
  const flush = () => {
    timer = null;
    if (!queue.length) return;
    const errors = queue; queue = [];
    try { send({ version: VERSION, errors }); } catch { /* Bericht darf nie selbst stören */ }
  };
  return {
    report(msg, stack) {
      const e = cleanError({ msg: String(msg || ''), stack: stack ? String(stack) : '' });
      if (!e.msg) return;
      const key = e.msg + '|' + (e.stack || '').split('\n')[1];
      if (seen.has(key) || seen.size >= max) return;
      seen.add(key);
      queue.push({ msg: e.msg, stack: e.stack, page: String(page() || '').split(/[?#]/)[0].slice(0, LIMITS.field) });
      if (!timer) timer = setTimeout(flush, delay);
    },
    flush,
  };
}

let reporting = false;
/** Einmal je Seite: Fehler zusätzlich an den Server melden (nur über https bzw. die echte Seite, nie im Test-Stub). */
export function installErrorReporter(target = typeof window !== 'undefined' ? window : null) {
  if (reporting || !target || typeof fetch === 'undefined') return null;
  reporting = true;
  const r = createReporter({
    send: (body) => fetch('/api/client-errors', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body), keepalive: true, credentials: 'omit' }).catch(() => {}),
  });
  target.addEventListener('error', (e) => r.report(e.message || (e.error && e.error.message) || '', e.error && e.error.stack));
  target.addEventListener('unhandledrejection', (e) => { const x = e.reason; r.report(x && x.message ? x.message : String(x), x && x.stack); });
  target.addEventListener('pagehide', () => r.flush());
  return r;
}

/** Was mitgehen würde — genau das zeigt die Vorschau im Formular. */
export function collectDiagnostics() {
  const nav = typeof navigator !== 'undefined' ? navigator : {};
  const html = typeof document !== 'undefined' ? document.documentElement : null;
  return {
    version: VERSION, build: BUILD_TIME,
    ua: cleanText(nav.userAgent || '', LIMITS.field),
    platform: cleanText((nav.userAgentData && nav.userAgentData.platform) || nav.platform || '', LIMITS.field),
    screen: typeof screen !== 'undefined' ? `${screen.width}x${screen.height}@${Math.round((window.devicePixelRatio || 1) * 100) / 100}` : null,
    viewport: typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : null,
    lang: html ? html.lang : null, locale: nav.language || null,
    page: typeof location !== 'undefined' ? cleanPage(location.href) : null,
    errors: recentErrors(),
  };
}
